// src/components/admin/StaffSchedule.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, ChevronLeft, Plus, Edit2, Trash2, X, Save, Eye, EyeOff } from 'lucide-react';
import { styles } from '../../styles/theme';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, onSnapshot } from '../../services/firebase';
import { getAvailabilityIndicator, getDayAvailabilityStatus, getShiftConflict } from '../../utils/staffAvailabilityHelper';
import { useStudentContext } from '../../contexts/StudentContext';
import { getDayStudentStats } from '../../utils/studentScheduleUtils';
import { useLocation } from '../../contexts/LocationContext';
import LocationSwitcher from '../shared/LocationSwitcher';

// ── Helpers ───────────────────────────────────────────────

const getWeekMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  if (day === 0) {
    d.setDate(d.getDate() + 1);
  } else {
    d.setDate(d.getDate() - day + 1);
  }
  d.setHours(0, 0, 0, 0);
  return d;
};

const toKey = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}-${d}-${y}`;
};

const to12Hour = (time) => {
  if (!time) return '';
  if (time.toLowerCase().includes('am') || time.toLowerCase().includes('pm')) return time;
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
};

const normalize = (str) => (str || '').toLowerCase().replace(/[-\s]+/g, '');

// Parse a time string like "9:00 AM" or "09:00" into decimal hours
const parseTimeToHours = (t) => {
  if (!t) return null;
  const s = t.trim().toUpperCase();
  const match = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
  if (!match) return null;
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const ampm = match[3];
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return h + m / 60;
};

const calcShiftHours = (startTime, endTime) => {
  const s = parseTimeToHours(startTime);
  const e = parseTimeToHours(endTime);
  if (s === null || e === null) return 0;
  return Math.max(0, e - s);
};

// Calculate total scheduled hours for a staff member in the current week
// excludeId: skip this shift (e.g. the one being edited) so it isn't counted twice
const calcWeeklyHours = (staffId, schedules, weekDays, excludeId = null) => {
  const weekDateKeys = new Set(weekDays.map(d => d.dateKey));
  return schedules
    .filter(s => s.staffId === staffId && weekDateKeys.has(s.date) && s.id !== excludeId)
    .reduce((sum, s) => sum + calcShiftHours(s.startTime, s.endTime), 0);
};

const HOURS_WARN = 35;  // yellow warning threshold
const HOURS_MAX  = 40;  // red / block threshold

const DAY_IDS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildWeekDays = (monday) =>
  DAY_IDS.map((id, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      id,
      label: DAY_LABELS[i],
      date: d,
      dateKey: toKey(d),
      dateLabel: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      isToday: toKey(d) === toKey(new Date()),
    };
  });

const EMPTY_FORM = { staffId: '', startTime: '', endTime: '', notes: '' };

const RoleBadge = ({ role }) => {
  if (!role || role === 'instructor') return null;
  const colors = {
    driver:      { bg: '#ede9fe', text: '#7c3aed' },
    admin_staff: { bg: '#dbeafe', text: '#1e40af' },
    operations:  { bg: '#d1fae5', text: '#065f46' },
    manager:     { bg: '#fef3c7', text: '#92400e' },
  };
  const c = colors[role] || { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{ fontSize: '0.6rem', fontWeight: '700', color: c.text, backgroundColor: c.bg, padding: '0.1rem 0.35rem', borderRadius: '3px', textTransform: 'capitalize', marginLeft: '0.25rem' }}>
      {role.replace('_', ' ')}
    </span>
  );
};

// ── Component ─────────────────────────────────────────────

const StaffSchedule = ({ onBack }) => {
  const { allStudents } = useStudentContext();
  const { currentLocation } = useLocation();
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [locations, setLocations] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [schedules, setSchedules] = useState([]);
  // All locations' shifts this week — used for weekly hours so Redmond + Kirkland shifts both count
  const [allLocationSchedules, setAllLocationSchedules] = useState([]);
  const [showCapacity, setShowCapacity] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [publishedWeeks, setPublishedWeeks] = useState({});
  const [publishSaving, setPublishSaving] = useState(false);
  const [weekAvailability, setWeekAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAvailPanel, setShowAvailPanel] = useState(true);
  const [modal, setModal] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // ── Computed week values ──
  const weekMonday = (() => {
    const m = getWeekMonday(new Date());
    m.setDate(m.getDate() + weekOffset * 7);
    return m;
  })();
  const weekSaturday = new Date(weekMonday);
  weekSaturday.setDate(weekMonday.getDate() + 5);
  const weekDays = buildWeekDays(weekMonday);
  const weekKey = toKey(weekMonday);
  const isPastWeek = weekOffset < 0;
  const weekLabel = `${weekMonday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekSaturday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  useEffect(() => {
    loadStaticData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  const loadStaticData = async () => {
    try {
      const [locSnap, progSnap, staffSnap] = await Promise.all([
        getDocs(collection(db, 'locations')),
        getDocs(collection(db, 'programs')),
        getDocs(collection(db, 'staff_members')),
      ]);

      const locFilter = (r) => currentLocation === 'all' || (r.locationId || 'redmond') === currentLocation;

      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.active && locFilter(l)));
      setPrograms(
        progSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false)
          .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99))
      );
      // Staff can float between locations — show all active staff
      setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.active));
    } catch (err) {
      console.error('Error loading static data:', err);
    }
  };

  // ── Load week data when week, staff, or location changes ──
  useEffect(() => {
    if (staff.length > 0) loadWeekData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset, staff.length, currentLocation]);

  const loadWeekData = async () => {
    setLoading(true);
    try {
      // Compute date keys fresh here to avoid stale closure
      const monday = getWeekMonday(new Date());
      monday.setDate(monday.getDate() + weekOffset * 7);
      const dateKeys = DAY_IDS.map((_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return toKey(d);
      });
      const wKey = toKey(monday);

      const schedSnap = await getDocs(collection(db, 'schedules'));
      const allSchedules = schedSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allLocationWeek = allSchedules.filter(s => dateKeys.includes(s.date));
      const weekSchedules = allLocationWeek.filter(s =>
        currentLocation === 'all' || (s.locationId || 'redmond') === currentLocation
      );
      setSchedules(weekSchedules);
      setAllLocationSchedules(allLocationWeek);

      // Load holidays filtered by location
      const holidaySnap = await getDocs(collection(db, 'holidays'));
      const allHolidays = holidaySnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHolidays(allHolidays.filter(h => currentLocation === 'all' || (h.locationId || 'redmond') === currentLocation));

      // Load publish status for this week
      const weekMetaRef = doc(db, 'schedule_weeks', wKey);
      const weekMetaSnap = await getDoc(weekMetaRef);
      setPublishedWeeks(prev => ({
        ...prev,
        [wKey]: weekMetaSnap.exists() ? weekMetaSnap.data().published === true : false,
      }));
    } catch (err) {
      console.error('Error loading week data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Live availability for the viewed week ──
  // onSnapshot so submissions made while the schedule is open show up (and flag conflicts) immediately
  const staffIdsKey = staff.map(s => s.id).join(',');
  useEffect(() => {
    setWeekAvailability({});
    const unsubscribes = staff.map(s =>
      onSnapshot(
        doc(db, 'staff_weekly_availability', s.id, 'weeks', weekKey),
        snap => setWeekAvailability(prev => ({ ...prev, [s.id]: snap.exists() ? snap.data() : null })),
        err => console.error('Error loading availability:', err)
      )
    );
    return () => unsubscribes.forEach(unsub => unsub());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, staffIdsKey]);

  // Availability status for one staff member on one day (from live data)
  const getStaffDayStatus = (staffId, dayId) =>
    getDayAvailabilityStatus(weekAvailability[staffId] || null, dayId);

  // Conflict reason for a shift vs. the staff member's submitted availability, or null
  const getAssignmentConflict = (shift, dayId) =>
    getShiftConflict(shift, weekAvailability[shift.staffId] || null, dayId);

  // ── Filtered programs ──
  // Uses center field (new) first, falls back to locationId (legacy)
  // Secondary dropdown removed — currentLocation context handles all filtering
  const filteredPrograms = programs.filter(p => {
    if (currentLocation !== 'all') {
      const centerVal = p.center; // 'redmond' | 'kirkland' | 'both'
      if (centerVal === 'both') {
        // show in both locations — pass through
      } else if (centerVal === 'kirkland' || centerVal === 'redmond') {
        if (centerVal !== currentLocation) return false;
      } else {
        // Legacy: no center field — use locationId fallback to redmond
        const progLocation = p.locationId === 'kirkland' ? 'kirkland' : 'redmond';
        if (progLocation !== currentLocation) return false;
      }
    }
    if (p.startDate) {
      const startObj = new Date(p.startDate + 'T00:00:00');
      if (startObj > weekSaturday) return false;
    }
    return true;
  });

  // ── Match assignments to a program slot ──
  // Handles both new records (have programId) and legacy records (programType + location)
  const getSlotAssignments = (program, dateKey) =>
    schedules.filter(s => {
      if (s.date !== dateKey) return false;
      // New records: exact programId match
      if (s.programId) return s.programId === program.id;
      // Legacy matching: check name, name-prefix, and legacyProgramTypes array
      const legacyTypes = program.legacyProgramTypes || [];
      const programMatch =
        normalize(program.name).startsWith(normalize(s.programType)) ||
        normalize(s.programType) === normalize(program.name) ||
        legacyTypes.some(t => normalize(t) === normalize(s.programType));
      const locationMatch =
        normalize(program.locationName).includes(normalize(s.location)) ||
        normalize(s.location).includes(normalize(program.locationName));
      return programMatch && locationMatch;
    });

  // ── Flexible = Full Day/Full-Day Camp (any spacing/hyphen variant) ──
  const isFlexible = (program) =>
    program.staffingType === 'flexible' ||
    normalize(program.name) === 'fulldaycamp';

  // ── Open assign modal ──
  const openAssignModal = (program, day, editingAssignment = null) => {
    // Get staff already assigned to this slot (excluding the one being edited)
    const alreadyAssignedIds = new Set(
      getSlotAssignments(program, day.dateKey)
        .filter(a => a.id !== editingAssignment?.id)
        .map(a => a.staffId)
    );
    setModal({ program, day, editingId: editingAssignment?.id || null, alreadyAssignedIds });
    setFormData(editingAssignment ? {
      staffId: editingAssignment.staffId,
      startTime: editingAssignment.startTime || '',
      endTime: editingAssignment.endTime || '',
      notes: editingAssignment.notes || '',
    } : {
      staffId: '',
      startTime: program.defaultStartTime ? to12Hour(program.defaultStartTime) : '',
      endTime: program.defaultEndTime ? to12Hour(program.defaultEndTime) : '',
      notes: '',
    });
  };

  const closeModal = () => {
    setModal(null);
    setFormData(EMPTY_FORM);
  };

  // Staff list for the picker, built from live availability: available → no submission → day off, then by name.
  // Everyone is selectable; day-off / outside-window picks show a red warning instead of being blocked.
  const AVAIL_SORT = { available: 0, 'no-data': 1, unavailable: 2 };
  const modalStaffList = modal
    ? staff
        .map(s => ({ ...s, availabilityCheck: getStaffDayStatus(s.id, modal.day.id) }))
        .sort((a, b) =>
          (AVAIL_SORT[a.availabilityCheck.status] ?? 1) - (AVAIL_SORT[b.availabilityCheck.status] ?? 1) ||
          `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`)
        )
    : [];

  const handleSave = async () => {
    if (!formData.staffId) { alert('Please select a staff member.'); return; }

    // Check weekly hours before saving
    if (formData.staffId && formData.startTime && formData.endTime) {
      const existingHours = calcWeeklyHours(formData.staffId, allLocationSchedules, weekDays, modal.editingId);
      const newShiftHours = calcShiftHours(formData.startTime, formData.endTime);
      const totalHours = existingHours + newShiftHours;
      if (totalHours > HOURS_MAX) {
        const staffMember = staff.find(s => s.id === formData.staffId);
        const name = staffMember ? staffMember.firstName : 'This staff member';
        if (!window.confirm(`⚠️ ${name} will be at ${totalHours.toFixed(1)}h this week (${existingHours.toFixed(1)}h already + ${newShiftHours.toFixed(1)}h new shift), exceeding the ${HOURS_MAX}h limit. Save anyway?`)) return;
      }
    }

    // Availability conflict (day off / outside window) — allowed, but confirm the override
    const conflict = getAssignmentConflict(formData, modal.day.id);
    if (conflict) {
      const staffMember = staff.find(s => s.id === formData.staffId);
      const name = staffMember ? staffMember.firstName : 'This staff member';
      if (!window.confirm(`⚠️ ${name}: ${conflict}. Schedule anyway?`)) return;
    }

    setSaving(true);
    try {
      const selectedStaffMember = staff.find(s => s.id === formData.staffId);
      const payload = {
        programId: modal.program.id,
        programName: modal.program.name,
        programType: modal.program.name,
        locationId: modal.program.center === 'kirkland' ? 'kirkland'
                  : modal.program.center === 'redmond'  ? 'redmond'
                  : modal.program.center === 'both'     ? (currentLocation !== 'all' ? currentLocation : 'redmond')
                  : modal.program.locationId === 'kirkland' ? 'kirkland'
                  : 'redmond',
        location: modal.program.locationName,
        date: modal.day.dateKey,
        dayOfWeek: modal.day.id.charAt(0).toUpperCase() + modal.day.id.slice(1),
        staffId: formData.staffId,
        staffName: `${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`,
        startTime: formData.startTime,
        endTime: formData.endTime,
        notes: formData.notes,
        updatedAt: serverTimestamp(),
      };
      if (modal.editingId) {
        await updateDoc(doc(db, 'schedules', modal.editingId), payload);
      } else {
        await addDoc(collection(db, 'schedules'), { ...payload, createdAt: serverTimestamp() });
      }
      closeModal();
      await loadWeekData();
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await deleteDoc(doc(db, 'schedules', id));
      await loadWeekData();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handlePublishWeek = async () => {
    const isPublished = publishedWeeks[weekKey] === true;
    const action = isPublished ? 'unpublish' : 'publish';
    if (!window.confirm(`${action === 'publish' ? 'Publish' : 'Unpublish'} this week's schedule? Staff will ${action === 'publish' ? 'be able to' : 'no longer be able to'} see it.`)) return;
    setPublishSaving(true);
    try {
      await setDoc(doc(db, 'schedule_weeks', weekKey), {
        published: !isPublished,
        updatedAt: serverTimestamp(),
      });
      setPublishedWeeks(prev => ({ ...prev, [weekKey]: !isPublished }));
    } catch (err) {
      alert('Failed to update publish status.');
    } finally {
      setPublishSaving(false);
    }
  };

  const [copying, setCopying] = useState(false);

  const handleCopyToNextWeek = async () => {
    const currentShifts = schedules.filter(s => {
      const [m, d, y] = (s.date || '').split('-').map(Number);
      if (!m || !d || !y) return false;
      const shiftDate = new Date(y, m - 1, d);
      const weekEnd = new Date(weekMonday);
      weekEnd.setDate(weekMonday.getDate() + 6);
      return shiftDate >= weekMonday && shiftDate <= weekEnd;
    });

    if (currentShifts.length === 0) {
      alert('No shifts in this week to copy.');
      return;
    }

    const nextWeekLabel = (() => {
      const m = new Date(weekMonday);
      m.setDate(m.getDate() + 7);
      return m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    })();

    if (!window.confirm(`Copy ${currentShifts.length} shift(s) from this week to the week of ${nextWeekLabel}? Existing shifts in that week will not be removed.`)) return;

    setCopying(true);
    try {
      const promises = currentShifts.map(shift => {
        const [m, d, y] = shift.date.split('-').map(Number);
        const nextDate = new Date(y, m - 1, d);
        nextDate.setDate(nextDate.getDate() + 7);
        const nm = String(nextDate.getMonth() + 1).padStart(2, '0');
        const nd = String(nextDate.getDate()).padStart(2, '0');
        const ny = nextDate.getFullYear();
        const newDateKey = `${nm}-${nd}-${ny}`;
        const { id, createdAt, updatedAt, ...rest } = shift;
        return addDoc(collection(db, 'schedules'), {
          ...rest,
          date: newDateKey,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      await Promise.all(promises);
      setWeekOffset(w => w + 1);
      alert(`✅ ${currentShifts.length} shift(s) copied to week of ${nextWeekLabel}.`);
    } catch (err) {
      console.error('Copy error:', err);
      alert('Failed to copy schedule. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  const getStaffDayAvail = (staffId, dayId) =>
    weekAvailability[staffId]?.days?.[dayId] || null;

  const getHolidaysForProgram = (program) => {
    return holidays.filter(h => {
      const hStart = h.startDate;
      const hEnd = h.endDate;
      const weekStart = toKey(weekMonday);
      const weekEnd = toKey(weekSaturday);
      const toISO = (mmddyyyy) => {
        const [m, d, y] = mmddyyyy.split('-');
        return `${y}-${m}-${d}`;
      };
      const wStartISO = toISO(weekStart);
      const wEndISO = toISO(weekEnd);
      const overlaps = hStart <= wEndISO && hEnd >= wStartISO;
      if (!overlaps) return false;
      if (h.type === 'all') return true;
      if (h.type === 'program') return h.entityId === program.id;
      if (h.type === 'school') {
        return normalize(h.entityName).includes(normalize(program.locationName)) ||
               normalize(program.locationName).includes(normalize(h.entityName));
      }
      return false;
    });
  };

  const formatHolidayDates = (h) => {
    const fmt = (iso) => {
      const [, m, d] = iso.split('-');
      return `${parseInt(m)}/${parseInt(d)}`;
    };
    return h.startDate === h.endDate ? fmt(h.startDate) : `${fmt(h.startDate)}–${fmt(h.endDate)}`;
  };

const getStaffDayAssignments = (staffId, dateKey) =>
  schedules.filter(s => {
    if (s.staffId !== staffId || s.date !== dateKey) return false;
    // Only show as assigned if the program still exists and is active
    if (s.programId) return programs.some(p => p.id === s.programId && p.active !== false);
    // Legacy: match by programType
    return programs.some(p =>
      normalize(p.name) === normalize(s.programType) ||
      (p.legacyProgramTypes || []).some(t => normalize(t) === normalize(s.programType))
    );
  });
  // ── Render ────────────────────────────────────────────────

  return (
    <div style={{ padding: '1rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* Top Bar */}
      <div style={{ marginBottom: '1rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 'bold', margin: 0 }}>📅 Staff Scheduler</h1>
            <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
              Assign staff to programs
              {currentLocation !== 'all' && (
                <span style={{ marginLeft: '0.5rem', fontWeight: '700', color: currentLocation === 'kirkland' ? '#7c3aed' : '#1e40af' }}>
                  — Stemtree {currentLocation === 'kirkland' ? 'Kirkland' : 'Redmond'}
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Location switcher */}
            <LocationSwitcher />
            <button
              onClick={() => setShowAvailPanel(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', backgroundColor: showAvailPanel ? '#eff6ff' : 'white', color: showAvailPanel ? '#1e40af' : '#374151', border: `1.5px solid ${showAvailPanel ? '#3b82f6' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
            >
              {showAvailPanel ? <Eye size={14} /> : <EyeOff size={14} />} Availability
            </button>
          </div>
        </div>

        {/* Week Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setWeekOffset(w => Math.max(w - 1, -4))}
            disabled={weekOffset <= -4}
            style={{ padding: '0.4rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', backgroundColor: 'white', cursor: weekOffset <= -4 ? 'not-allowed' : 'pointer', opacity: weekOffset <= -4 ? 0.4 : 1 }}
          >
            <ChevronLeft size={16} />
          </button>

          {[-4, -3, -2, -1, 0, 1, 2, 3].map(offset => {
            const m = getWeekMonday(new Date());
            m.setDate(m.getDate() + offset * 7);
            const isSelected = offset === weekOffset;
            const isPast = offset < 0;
            const isCurrent = offset === 0;
            const label = isCurrent ? 'This Week' : offset === 1 ? 'Next Week' : m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return (
              <button
                key={offset}
                onClick={() => setWeekOffset(offset)}
                style={{ padding: '0.3rem 0.55rem', border: '1.5px solid', borderColor: isSelected ? '#3b82f6' : '#e5e7eb', borderRadius: '6px', backgroundColor: isSelected ? '#3b82f6' : isCurrent ? '#f0fdf4' : 'white', color: isSelected ? 'white' : isPast ? '#9ca3af' : '#374151', fontSize: '0.72rem', fontWeight: isSelected ? '700' : '400', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {label}
              </button>
            );
          })}

          <button
            onClick={() => setWeekOffset(w => Math.min(w + 1, 3))}
            disabled={weekOffset >= 3}
            style={{ padding: '0.4rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', backgroundColor: 'white', cursor: weekOffset >= 3 ? 'not-allowed' : 'pointer', opacity: weekOffset >= 3 ? 0.4 : 1 }}
          >
            <ChevronRight size={16} />
          </button>

          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151' }}>{weekLabel}</span>
          {isPastWeek && (
            <span style={{ fontSize: '0.72rem', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.2rem 0.6rem', borderRadius: '4px', fontWeight: '600' }}>
              📋 Read Only
            </span>
          )}

          {!isPastWeek && (
            <>
              <span style={{
                fontSize: '0.72rem', fontWeight: '700', padding: '0.2rem 0.65rem', borderRadius: '4px',
                backgroundColor: publishedWeeks[weekKey] ? '#d1fae5' : '#fef3c7',
                color: publishedWeeks[weekKey] ? '#065f46' : '#92400e',
              }}>
                {publishedWeeks[weekKey] ? '✅ Published' : '📝 Draft'}
              </span>
              <button
                onClick={handlePublishWeek}
                disabled={publishSaving}
                style={{
                  fontSize: '0.72rem', fontWeight: '700', padding: '0.2rem 0.75rem', borderRadius: '4px',
                  backgroundColor: publishedWeeks[weekKey] ? '#fee2e2' : '#3b82f6',
                  color: publishedWeeks[weekKey] ? '#dc2626' : 'white',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {publishSaving ? '...' : publishedWeeks[weekKey] ? 'Unpublish' : 'Publish to Staff'}
              </button>
              <button
                onClick={handleCopyToNextWeek}
                disabled={copying}
                style={{
                  fontSize: '0.72rem', fontWeight: '700', padding: '0.2rem 0.75rem', borderRadius: '4px',
                  backgroundColor: copying ? '#9ca3af' : '#8b5cf6',
                  color: 'white', border: 'none', cursor: copying ? 'not-allowed' : 'pointer',
                }}
              >
                {copying ? '...' : '📋 Copy to Next Week'}
              </button>
            </>
          )}
          <button
            onClick={() => setShowCapacity(c => !c)}
            style={{ fontSize: '0.72rem', fontWeight: '700', padding: '0.2rem 0.75rem', borderRadius: '4px', backgroundColor: showCapacity ? '#1e40af' : '#e0f2fe', color: showCapacity ? 'white' : '#1e40af', border: 'none', cursor: 'pointer' }}
          >
            📊 {showCapacity ? 'Hide' : 'Staff Hours'}
          </button>
        </div>
      </div>

      {/* Staff Capacity Panel */}
      {showCapacity && (
        <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            📊 Staff Hours This Week
            <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '400' }}>— max {HOURS_MAX}h per staff</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
            {staff.filter(s => s.active !== false).map(s => {
              const hrs = calcWeeklyHours(s.id, allLocationSchedules, weekDays);
              const pct = Math.min((hrs / HOURS_MAX) * 100, 100);
              const color = hrs >= HOURS_MAX ? '#dc2626' : hrs >= HOURS_WARN ? '#d97706' : '#059669';
              const bg = hrs >= HOURS_MAX ? '#fef2f2' : hrs >= HOURS_WARN ? '#fffbeb' : '#f0fdf4';
              return (
                <div key={s.id} style={{ backgroundColor: bg, borderRadius: '8px', padding: '0.6rem 0.75rem', border: `1px solid ${hrs >= HOURS_MAX ? '#fecaca' : hrs >= HOURS_WARN ? '#fde68a' : '#bbf7d0'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1f2937' }}>{s.firstName} {s.lastName?.charAt(0)}.</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: '800', color }}>{hrs.toFixed(1)}h</span>
                  </div>
                  <div style={{ height: '5px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '3px', transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '0.2rem' }}>
                    {hrs === 0 ? 'No shifts yet' : hrs >= HOURS_MAX ? '🔴 Over limit' : hrs >= HOURS_WARN ? '🟡 Near limit' : `🟢 ${(HOURS_MAX - hrs).toFixed(1)}h remaining`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>

        {/* Schedule Grid */}
        <div style={{ flex: 1, overflowX: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>Loading schedule...</div>
          ) : filteredPrograms.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>No active programs. Add programs via Admin → Programs.</div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>

              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '175px repeat(6, 1fr)', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <div style={{ padding: '0.75rem 1rem', fontSize: '0.72rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase' }}>Program</div>
                {weekDays.map(day => (
                  <div key={day.id} style={{ padding: '0.6rem 0.4rem', textAlign: 'center', borderLeft: '1px solid #e5e7eb', backgroundColor: day.isToday ? '#eff6ff' : 'transparent' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: '700', color: day.isToday ? '#1e40af' : '#374151' }}>{day.label}{day.isToday ? ' 📍' : ''}</div>
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{day.dateLabel}</div>
                  </div>
                ))}
              </div>

              {/* Student Stats Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '175px repeat(6, 1fr)', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0fdf4' }}>
                <div style={{ padding: '0.4rem 1rem', borderRight: '1px solid #e5e7eb', display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.62rem', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>👥 Students</span>
                </div>
                {weekDays.map(day => {
                  const [m, d, y] = day.dateKey.split('-').map(Number);
                  const dayDate = new Date(y, m - 1, d);
                  const stats = getDayStudentStats(allStudents, dayDate);
                  return (
                    <div key={day.id} style={{ padding: '0.4rem 0.25rem', textAlign: 'center', borderLeft: '1px solid #e5e7eb', backgroundColor: day.isToday ? '#dcfce7' : 'transparent' }}>
                      {stats.total === 0 ? (
                        <span style={{ fontSize: '0.65rem', color: '#d1d5db' }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#16a34a', lineHeight: 1 }}>{stats.total}</span>
                          <div style={{ display: 'flex', gap: '0.15rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {stats.camp > 0 && (
                              <span title={`${stats.camp - stats.bhCamp} website + ${stats.bhCamp} Bright Horizons`} style={{ fontSize: '0.55rem', fontWeight: '700', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.05rem 0.25rem', borderRadius: '3px' }}>
                                Camp {stats.camp}
                              </span>
                            )}
                            {stats.pickup > 0 && (
                              <span style={{ fontSize: '0.55rem', fontWeight: '700', backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.05rem 0.25rem', borderRadius: '3px' }}>
                                Pickup {stats.pickup}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Program Rows */}
              {filteredPrograms.map((program, pIdx) => (
                <div
                  key={program.id}
                  style={{ display: 'grid', gridTemplateColumns: '175px repeat(6, 1fr)', borderBottom: pIdx < filteredPrograms.length - 1 ? '1px solid #e5e7eb' : 'none', minHeight: '80px' }}
                >
                  {/* Program Label */}
                  <div style={{ padding: '0.75rem', borderRight: '1px solid #e5e7eb', backgroundColor: '#fafafa', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontWeight: '700', fontSize: '0.82rem', color: '#1f2937', lineHeight: 1.3 }}>{program.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#3b82f6', fontWeight: '600', marginTop: '0.1rem' }}>{program.locationName}</div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.1rem' }}>
                      {isFlexible(program) ? 'Flexible staffing' : `Needs ${program.defaultStaffNeeded}`}
                    </div>
                    {program.startDate && (
                      <div style={{ fontSize: '0.62rem', color: '#f59e0b', fontWeight: '600', marginTop: '0.15rem' }}>Starts {program.startDate}</div>
                    )}
                    {getHolidaysForProgram(program).map(h => (
                      <div key={h.id} style={{ marginTop: '0.25rem', fontSize: '0.6rem', fontWeight: '600', color: '#b45309', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '3px', padding: '0.1rem 0.3rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                        🏖️ {h.name} {formatHolidayDates(h)}
                      </div>
                    ))}
                  </div>

                  {/* Day Cells */}
                  {weekDays.map(day => {
                    const isActive = program.activeDays?.includes(day.id);
                    if (!isActive) return (
                      <div key={day.id} style={{ borderLeft: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '0.65rem', color: '#e5e7eb' }}>—</span>
                      </div>
                    );

                    const assignments = getSlotAssignments(program, day.dateKey);
                    const flexible = isFlexible(program);
                    const minStaff = program.defaultStaffNeeded || 1;
                    const showWarning = flexible ? assignments.length === 0 : assignments.length < minStaff;

                    return (
                      <div
                        key={day.id}
                        style={{ borderLeft: '1px solid #e5e7eb', padding: '0.4rem', backgroundColor: day.isToday ? '#fafcff' : 'white', display: 'flex', flexDirection: 'column', gap: '0.3rem', minHeight: '80px' }}
                      >
                        {assignments.map(a => {
                          const conflict = getAssignmentConflict(a, day.id);
                          return (
                          <div key={a.id} title={conflict ? `⚠️ ${conflict}` : undefined} style={{ backgroundColor: conflict ? '#fef2f2' : '#eff6ff', border: `1px solid ${conflict ? '#fca5a5' : '#bfdbfe'}`, borderRadius: '6px', padding: '0.3rem 0.4rem' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: conflict ? '#b91c1c' : '#1e40af', lineHeight: 1.2 }}>{conflict ? '⚠️ ' : ''}{a.staffName}</div>
                            {(a.startTime || a.endTime) && (
                              <div style={{ fontSize: '0.65rem', color: '#3b82f6', marginTop: '0.1rem' }}>
                                {a.startTime}{a.startTime && a.endTime ? ' – ' : ''}{a.endTime}
                              </div>
                            )}
                            {a.notes && (
                              <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: '0.15rem', fontStyle: 'italic', borderTop: '1px solid #dbeafe', paddingTop: '0.15rem' }}>
                                📝 {a.notes}
                              </div>
                            )}
                            {!isPastWeek && (
                              <div style={{ display: 'flex', gap: '0.2rem', marginTop: '0.2rem' }}>
                                <span style={{ fontSize: '0.6rem', color: '#6b7280', fontWeight: '600', backgroundColor: (a.locationId || currentLocation) === 'kirkland' ? '#d1fae5' : '#dbeafe', padding: '0.1rem 0.3rem', borderRadius: '3px', marginRight: '0.2rem' }}>
                                  {(a.locationId || currentLocation) === 'kirkland' ? 'KL' : 'RD'}
                                </span>
                                <button onClick={() => openAssignModal(program, day, a)} style={{ padding: '0.15rem 0.3rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                                  <Edit2 size={9} />
                                </button>
                                <button onClick={() => handleDelete(a.id)} style={{ padding: '0.15rem 0.3rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                                  <Trash2 size={9} />
                                </button>
                              </div>
                            )}
                          </div>
                          );
                        })}

                        {!isPastWeek && (
                          <button
                            onClick={() => openAssignModal(program, day)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.2rem', padding: '0.25rem', backgroundColor: showWarning ? '#fffbeb' : '#f9fafb', border: `1px dashed ${showWarning ? '#f59e0b' : '#d1d5db'}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.65rem', color: showWarning ? '#92400e' : '#9ca3af', fontWeight: showWarning ? '600' : '400', marginTop: 'auto' }}
                          >
                            <Plus size={9} />
                            {showWarning
                              ? (flexible ? 'Add staff shift' : `Need ${minStaff - assignments.length} more`)
                              : '+ Add'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Availability Panel */}
        {showAvailPanel && (
          <div style={{ width: '255px', flexShrink: 0, backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '0.65rem 1rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151' }}>👥 Staff Availability</div>
              <div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Week of {weekKey}</div>
            </div>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '75px repeat(6, 1fr)', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
              <div style={{ padding: '0.35rem' }} />
              {weekDays.map(day => (
                <div key={day.id} style={{ padding: '0.35rem 0.1rem', textAlign: 'center', fontSize: '0.6rem', fontWeight: '700', color: day.isToday ? '#1e40af' : '#6b7280', borderLeft: '1px solid #e5e7eb' }}>
                  {day.label}
                </div>
              ))}
            </div>

            {/* Staff rows */}
            <div style={{ overflowY: 'auto', maxHeight: '55vh' }}>
              {staff.map((s, idx) => (
                <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '75px repeat(6, 1fr)', borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                  <div style={{ padding: '0.35rem 0.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: '600', color: '#1f2937', lineHeight: 1.2 }}>
                      {s.firstName} {s.lastName.charAt(0)}.
                    </div>
                    <RoleBadge role={s.role} />
                  </div>
                  {weekDays.map(day => {
                    const avail = getStaffDayAvail(s.id, day.id);
                    const assigned = getStaffDayAssignments(s.id, day.dateKey);
                    const noData = !weekAvailability[s.id];
                    let bg = 'transparent';
                    let content;

                    const conflicts = assigned.map(a => getAssignmentConflict(a, day.id)).filter(Boolean);

                    if (conflicts.length > 0) {
                      // Assigned on a day off / outside their window — don't let the 📌 hide the ✗
                      bg = '#fee2e2';
                      content = <span title={`⚠️ ${[...new Set(conflicts)].join('\n')}`} style={{ fontSize: '0.6rem' }}>⚠️</span>;
                    } else if (noData) {
                      content = <span title="No availability submitted" style={{ fontSize: '0.6rem', color: '#9ca3af' }}>{assigned.length > 0 ? '📌' : '?'}</span>;
                    } else if (assigned.length > 0) {
                      bg = '#dbeafe';
                      content = <span title={assigned.map(a => a.programName || a.programType).join(', ')} style={{ fontSize: '0.6rem' }}>📌</span>;
                    } else if (avail?.available) {
                      bg = '#f0fdf4';
                      content = (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.6rem', color: '#15803d', fontWeight: '700' }}>✓</div>
                          {avail.startTime && (
                            <div style={{ fontSize: '0.48rem', color: '#6b7280', lineHeight: 1 }}>
                              {to12Hour(avail.startTime).replace(' ', '')}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      content = <span style={{ fontSize: '0.6rem', color: '#fca5a5' }}>✗</span>;
                    }

                    return (
                      <div key={day.id} style={{ borderLeft: '1px solid #f3f4f6', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '34px', padding: '0.15rem' }}>
                        {content}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[['✓', '#15803d', 'Available'], ['📌', '#1e40af', 'Assigned'], ['✗', '#f87171', 'Unavailable'], ['⚠️', '#dc2626', 'Conflict'], ['?', '#9ca3af', 'No submission']].map(([sym, col, lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.6rem', color: '#6b7280' }}>
                  <span style={{ color: col }}>{sym}</span> {lbl}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>
                  {modal.editingId ? 'Edit Assignment' : 'Assign Staff'}
                </h2>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {modal.program.name} @ {modal.program.locationName} — {modal.day.label} {modal.day.dateLabel}
                </p>
              </div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <X size={20} />
              </button>
            </div>

            {/* Staff picker */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>
                Staff Member *
              </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '220px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.4rem' }}>
                  {modalStaffList.map(s => {
                    const avail = s.availabilityCheck;
                    const isSelected = formData.staffId === s.id;
                    const indicator = getAvailabilityIndicator(avail.status);
                    const alreadyAssigned = modal?.alreadyAssignedIds?.has(s.id);
                    // Day off or entered times outside their window → red warning (still selectable, to override)
                    const conflict = getAssignmentConflict({ staffId: s.id, startTime: formData.startTime, endTime: formData.endTime }, modal.day.id);
                    const isClickable = !alreadyAssigned;
                    return (
                        <div
                          key={s.id}
                          title={conflict ? `⚠️ ${conflict}` : undefined}
                          onClick={() => isClickable && setFormData(f => ({ ...f, staffId: s.id }))}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.45rem 0.65rem', borderRadius: '6px', border: '1.5px solid', borderColor: isSelected ? '#3b82f6' : alreadyAssigned ? '#fbbf24' : conflict ? '#fca5a5' : '#e5e7eb', backgroundColor: isSelected ? '#eff6ff' : alreadyAssigned ? '#fffbeb' : conflict ? '#fef2f2' : 'white', cursor: isClickable ? 'pointer' : 'not-allowed', opacity: isClickable ? 1 : 0.6 }}
                        >
                          <div>
                            <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1f2937' }}>
                              {indicator} {s.firstName} {s.lastName}
                            </span>
                            <RoleBadge role={s.role} />
                            {alreadyAssigned && (
                              <div style={{ fontSize: '0.68rem', color: '#92400e', marginTop: '0.1rem', fontWeight: '600' }}>✅ Already assigned this slot</div>
                            )}
                            {!alreadyAssigned && conflict && (
                              <div style={{ fontSize: '0.68rem', color: '#b91c1c', marginTop: '0.1rem', fontWeight: '600' }}>⚠️ {conflict}</div>
                            )}
                            {!alreadyAssigned && !conflict && avail.status === 'no-data' && (
                              <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: '0.1rem' }}>No availability submitted</div>
                            )}
                          </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.15rem', flexShrink: 0 }}>
                          {(() => {
                            const wkHrs = calcWeeklyHours(s.id, allLocationSchedules, weekDays, modal.editingId);
                            const newHrs = calcShiftHours(formData.startTime, formData.endTime);
                            const total = wkHrs + newHrs;
                            // Colour by the total after this shift, so a shift that would push them over shows red
                            const color = total >= HOURS_MAX ? '#dc2626' : total >= HOURS_WARN ? '#d97706' : '#059669';
                            return (
                              <span style={{ fontSize: '0.68rem', fontWeight: '700', color, backgroundColor: total >= HOURS_MAX ? '#fef2f2' : total >= HOURS_WARN ? '#fffbeb' : '#f0fdf4', padding: '0.1rem 0.35rem', borderRadius: '4px' }}>
                                {wkHrs.toFixed(1)}h{newHrs > 0 ? ` → ${total.toFixed(1)}h` : ''}
                              </span>
                            );
                          })()}
                          {avail.available && avail.startTime && (
                            <div style={{ fontSize: '0.65rem', color: '#6b7280', textAlign: 'right' }}>
                              {to12Hour(avail.startTime)} – {to12Hour(avail.endTime)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
            </div>

            {/* Times */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>Start Time</label>
                <input type="text" value={formData.startTime} onChange={e => setFormData(f => ({ ...f, startTime: e.target.value }))} placeholder="3:15 PM" style={styles.input} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>End Time</label>
                <input type="text" value={formData.endTime} onChange={e => setFormData(f => ({ ...f, endTime: e.target.value }))} placeholder="5:30 PM" style={styles.input} />
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem' }}>Notes (visible on schedule)</label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Pick up materials, bring green kit..."
                rows={2}
                style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={closeModal} style={{ ...styles.button, flex: 1, backgroundColor: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                <X size={15} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.staffId}
                style={{ ...styles.button, flex: 1, backgroundColor: saving || !formData.staffId ? '#9ca3af' : '#10b981', cursor: saving || !formData.staffId ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
              >
                <Save size={15} /> {saving ? 'Saving...' : modal.editingId ? 'Update' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffSchedule;
